import { InfoCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { useUpdateEffect, useSize } from 'ahooks';
import { Divider, Spin, Tabs, Tooltip } from 'antd';
import { initEcharts, EChartsOption } from 'lib/echarts';
import { useFlagrDashboardRankValue } from 'hooks/business/system/useFlagr';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { assign, curry, isArray, isNil, keys, pick } from 'lodash-es';

import { formatNumber } from 'utils/numberFormat';
import { isAeProduct } from 'productLine';
import { useEffect, useMemo, useRef } from 'react';
import { TFnType, useTranslate } from '@tolgee/react';
import { useAsync } from 'react-use';
import { getOrderWastage } from 'services/api/dashboard';
import {
  momentDateRange2FetchParams,
  numDisplay,
  percentDisplay,
  platformAll2Undefined,
  setEchartsDefaultValue,
  storeType2FetchParams,
} from '../../utils';
import AheadButton, { calcAhead } from '../business/AheadButton';
import { useDashboardFilter, useDashboardPlatformTips } from '../Filter/useFilter';
import useDrawerContext from '../Trend/hooks/useDrawer';
import TitleLayout from '../Trend/TitleLayout';
import convertData, {
  OrderWastageCoveredData,
  StatsName,
  statsNameToPerformanceName,
} from './convertData';
import {
  CommonContainer as CContainer,
  CommonContentContainer,
  CommonTitleContainer,
} from './Recover';
import { mercadoNewDataDate } from '../Filter/Provider';

const Container = styled(CContainer)`
  border-bottom: 0;
  padding-bottom: 16px;
  border-radius: 4px 4px 0 0;
  border: none;
  .ant-card-body {
    padding: 0;
  }
  .ant-tabs {
    position: sticky;
    top: 62px;
    margin-top: -2px;
    background: var(--fett-bg-panel);
    z-index: 1;
  }
`;
const TitleContainer = styled(CommonTitleContainer)``;
const ContentContainer = styled(CommonContentContainer)`
  margin: 0;
  border: 0;
`;
const TabTips = styled.div`
  background: var(--fett-bg-info-tip);
  padding: 6px 10px;
  svg {
    color: #5175e1;
    margin-right: 6px;
    border-radius: 4px;
  }
`;
const Relative = styled.div`
  position: relative;
  width: 100%;
  border: 1px solid var(--fett-border-default);
  border-width: 0 1px 1px 1px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  .left {
    width: 100%;
    display: flex;
    flex: none;
    gap: 16px;
    align-self: center;
    .item {
      width: 200px;
      height: 80px;
      background: #f0f6ff;
      border: 1px solid rgba(162, 186, 255, 1);
      display: flex;
      flex-direction: column;
      justify-content: center;
      border-radius: 4px;
      padding: 0 8px;
      [data-color-mode='dark'] & {
        background: var(--fett-bg-active);
        border-color: var(--fett-border-strong);
      }
      .title {
        color: var(--fett-text-tertiary);
        text-align: left;
        svg {
          margin-left: 4px;
        }
      }
      .val {
        font-size: 20px;
        font-weight: 500;
        text-align: left;
      }
    }
    .wastage-cod-item {
      .val {
        display: flex;
        margin-top: 5px;
        gap: 5px;
      }
    }
  }
  .el {
    height: 280px;
    width: 100%;
  }
`;

const renderRichTagCurry = curry((tag: string, content: string) => `{${tag}|${content}}`);
const renderWrapSpaceCurry = curry((wrap: boolean, offset: number = 1) => {
  if (!wrap) {
    return '';
  }
  return ' '.repeat(offset);
});
/**
 * @description 似乎echarts 富文本没有left一类的布局属性
 * @description 使用空白字符串替代
 */
const padTextGen = (pos: 'start' | 'end') => {
  return (raw: string, offset?: number) => {
    const basicOffset = ' ';
    const offsetFinal = basicOffset.repeat(offset ?? 2);
    return pos !== 'end' ? `${offsetFinal}${raw}` : `${raw}${offsetFinal}`;
  };
};
const padText = padTextGen('start');
const padTextRight = padTextGen('end');

const noData = (value?: Dashboard.OrderWastageInner[keyof Dashboard.OrderWastageInner]) => {
  if (!value) {
    return true;
  }
  return Object.values(Object.values<any>(value) || {}).every((x: any) => {
    return Object.values(x || {}).every((num) => {
      // 领先比率可能为 -1
      if (num === -1) {
        return true;
      }
      return !num;
    });
  });
};

interface Props {
  tab: '非COD订单损耗情况' | 'COD订单损耗情况';
  setTab: (tab: '非COD订单损耗情况' | 'COD订单损耗情况') => void;
}

const convertOptions = (
  value: Dashboard.OrderWastageInner[keyof Dashboard.OrderWastageInner],
  t: TFnType,
  rankEnable = false,
  isWrap: boolean,
): EChartsOption => {
  const xAxisData: any = Object.keys(value || {}).map((x) => {
    return {
      value: t(`ViewDashboard.${x}`),
      textStyle: {
        align: 'left',
      },
    };
  });
  const stages = Object.values(value || {});
  const max = Math.max(...stages.map((x) => x.订单留存量 || 0));
  const noValue = noData(value);

  const basicOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        label: {
          backgroundColor: '#6a7985',
        },
      },
      formatter: (params: any) => {
        const target = params.find((x: any) => x.seriesName === t('ViewDashboard.留存订单量'));
        const { name, seriesName, value } = target;
        return `${name}<br/>${seriesName}: ${numDisplay(value)}<br/>${t(
          '留存订单率',
        )}: ${percentDisplay(value / max)}`;
      },
    },

    yAxis: [
      {
        type: 'value',
        show: false,
        inverse: true,
      },
    ],
    grid: {
      top: rankEnable ? 108 : 68,
      left: 24,
      bottom: 0,
      right: isWrap ? 100 : '10%',
    },
    xAxis: [
      {
        type: 'category',
        data: xAxisData as any,
        position: 'top',
        boundaryGap: false,
        axisLabel: {
          padding: [0, 0, rankEnable ? 80 : 38, 0],
          fontWeight: 900,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            width: 2,
            color: '#807f7f',
          },
        },
      },
    ],
    series: [
      {
        name: t('ViewDashboard.订单损耗量'),
        type: 'line',
        stack: 'Total',
        areaStyle: {
          color: '#e84825',
          opacity: 0.25,
        },
        emphasis: {
          disabled: true,
        },
        data: stages.map((x) => max - (x.订单留存量 || 0)),
        tooltip: {
          show: false,
        },
        endLabel: {
          formatter: () => t('ViewDashboard.订单损耗'),
          show: !noValue,
          align: 'right',
          offset: [-20, -14],
          verticalAlign: 'top',
          color: 'var(--fett-text-primary)',
          textBorderColor: 'transparent',
          textBorderWidth: 0,
        },
        markLine: {
          symbol: ['none', 'none'],
          lineStyle: {
            type: 'dashed',
            width: 2,
            color: 'transparent',
          },

          data: stages.map((x, index) => {
            return {
              xAxis: index,
              type: 'max',
              label: {
                formatter: () => {
                  if (index === stages.length - 1) {
                    return '';
                  }
                  const renderA = renderRichTagCurry('a');
                  const lines: string[] = [
                    padText(
                      renderA(
                        `${padTextRight(`${t('ViewDashboard.订单损耗量')}:`)}${padText(
                          numDisplay(x.订单损耗量),
                          0,
                        )}`,
                      ),
                    ),
                    padText(
                      renderA(
                        `${padTextRight(`${t('ViewDashboard.订单损耗率(图表)')}:`)} ${padText(
                          percentDisplay(x.订单损耗率),
                          0,
                        )}`,
                      ),
                    ),
                  ];
                  return lines.join(`\n`);
                },
                fontSize: 14,
                lineHeight: 14,
                distance: [0, rankEnable ? 55 : 8],
                position: 'start',
                overflow: 'break',
                align: 'left',
                color: 'var(--fett-text-primary)',
                textBorderColor: 'transparent',
                textBorderWidth: 0,
                rich: {
                  a: {
                    align: 'left',
                  },
                },
              },
            };
          }),
        },
      },
      {
        name: t('ViewDashboard.留存订单量'),
        stack: 'Total',
        type: 'line',
        areaStyle: {
          color: '#5b8ff9',
          opacity: 0.25,
        },
        lineStyle: {
          color: 'transparent',
        },
        itemStyle: {
          color: 'transparent',
        },

        endLabel: {
          formatter: () => t('ViewDashboard.留存订单'),
          show: !noValue,
          align: 'right',
          offset: [-20, -45],
          verticalAlign: 'top',
        },
        emphasis: {
          disabled: true,
        },
        data: Object.values(value || {}).map((x) => x.订单留存量 || 0),
      },
    ],
  };

  if (rankEnable) {
    const pkSeries: Required<EChartsOption>['series'] = {
      name: 'pk-series',
      type: 'line',
      stack: 'Total',
      areaStyle: {
        color: '#e84825',
        opacity: 0,
      },
      emphasis: {
        disabled: true,
      },
      data: xAxisData,
      tooltip: {
        show: false,
      },
      markLine: {
        symbol: ['none', 'none'],
        lineStyle: {
          type: 'dashed',
          width: 0,
          color: 'transparent',
        },

        data: stages.map((_x, index) => {
          const rate = stages[index].领先比率 ?? 0;
          const isAhead = calcAhead(rate);
          const color = isAhead ? '#3366ff' : '#ff433d';
          return {
            xAxis: xAxisData[index].value,

            type: 'max',
            label: {
              formatter: () => {
                if (index === stages.length - 1) {
                  return '';
                }
                const renderPkIcon = renderRichTagCurry('pk');
                return renderPkIcon('PK');
              },

              fontSize: 13,
              distance: 20,
              position: 'start',
              overflow: 'break',
              show: !noValue,
              align: 'left',
              borderWidth: 1,
              borderRadius: [4, 0, 0, 4],
              borderColor: color,
              textBorderWidth: 1,
              padding: 0,
              backgroundColor: 'var(--fett-bg-panel)',
              color,
              height: 26,
              lineHeight: 26,
              verticalAlign: 'middle',
              rich: {
                pk: {
                  height: 26,
                  lineHeight: 26,
                  width: 25,
                  color: '#fff',
                  backgroundColor: color,
                  fontStyle: 'italic',
                  fontWeight: 'bold',
                  backgroundSize: 10,
                  fontSize: 14,
                  align: 'center',
                  borderRadius: [4, 0, 0, 4],
                },
              },
            },
          };
        }),
      },
    };
    const tSeries: Required<EChartsOption>['series'] = assign({}, pkSeries, {
      name: 't-series',
      markLine: {
        symbol: ['none', 'none'],
        lineStyle: {
          type: 'dashed',
          width: 0,
          color: 'transparent',
        },
        data: stages.map((_, index) => {
          const rate = stages[index].领先比率 ?? 0;
          const isAhead = calcAhead(rate);
          const color = isAhead ? '#3366ff' : '#ff433d';
          return {
            xAxis: xAxisData[index].value,
            type: 'max',
            label: {
              formatter: () => {
                if (index === stages.length - 1) {
                  return '';
                }
                const renderAhead = renderRichTagCurry('t');
                const rankText = t('ViewDashboard.领先');
                const suffixText = t('ViewDashboard.同行');
                const wrap = isWrap ? '\n' : '';
                const wrapSpace = renderWrapSpaceCurry(isWrap);

                return padTextRight(
                  renderAhead(
                    `${rankText}${wrapSpace}${
                      rate < 0 ? '-' : formatNumber(rate, '0%')
                    }${wrapSpace}${wrap}${suffixText}`,
                  ),
                  1,
                );
              },
              fontSize: 13,
              distance: 20,
              position: 'start',
              overflow: 'break',
              show: !noValue,
              align: 'left',
              borderColor: color,
              borderWidth: 1,
              borderRadius: [0, 4, 4, 0],
              padding: 0,
              backgroundColor: 'var(--fett-bg-panel)',
              color,
              height: 26,
              verticalAlign: 'middle',
              offset: [26, 0],
              rich: {
                t: {
                  padding: [0, 0, 0, 5],
                  backgroundColor: 'var(--fett-bg-panel)',
                  lineHeight: isWrap ? 13 : 26,
                  borderRadius: [0, 4, 4, 0],
                  borderWidth: 1,
                },
              },
            },
          };
        }),
      },
    });
    if (isArray(basicOptions.series)) {
      basicOptions.series.splice(1, 0, pkSeries);
      basicOptions.series.splice(1, 0, tSeries);
    } else {
      basicOptions.series = pkSeries;
      basicOptions.series = tSeries;
    }
  }

  return basicOptions;
};
const getType = (platform?: any, tab?: any) => {
  if (platform === 'MERCADO') {
    return 'lazada非COD订单损耗情况';
  }

  if (platform === 'LAZADA') {
    return tab === 'COD订单损耗情况' ? 'lazadaCOD订单损耗情况' : 'lazada非COD订单损耗情况';
  }

  return tab === 'COD订单损耗情况' ? 'shopeeCOD订单损耗情况' : 'shopee非COD订单损耗情况';
};

const Wastage = (props: Props) => {
  const { tab, setTab } = props;
  const rankEnableFlagr = useFlagrDashboardRankValue();
  const { t } = useTranslate();
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};
  const filterCtx = useDashboardFilter();
  const { setTrendValue } = useDrawerContext();
  const isCod = useMemo(() => (tab === '非COD订单损耗情况' ? 0 : 1), [tab]);
  const fetchParams: Dashboard.BasicParams = useMemo(() => {
    const list = filterCtx.storeInfo.list || [];
    return {
      ...momentDateRange2FetchParams(filterCtx.dateRange),
      platform: platformAll2Undefined(filterCtx.platform),
      storeType: storeType2FetchParams(filterCtx.storeType),
      storeIds: list.map((x) => x.id),
      isCod,
      departmentIds: filterCtx.departments?.map((_) => _.id),
    };
  }, [
    filterCtx.platform,
    filterCtx.dateRange,
    filterCtx.storeType,
    filterCtx.storeInfo.list,
    isCod,
    filterCtx.departments,
  ]);
  const { value: { charts: value, lossStats } = {}, loading } = useAsync<
    () => Promise<OrderWastageCoveredData | undefined>
  >(() => {
    if (!fetchParams.platform) {
      return Promise.resolve(undefined);
    }
    return getOrderWastage(fetchParams).then(convertData);
  }, [fetchParams]);
  const language = useSystemLanguageValue();
  const isWrap = language !== 'zh-CN';
  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);
  useUpdateEffect(() => {
    setTrendValue({
      visible: false,
    });
  }, [tab]);

  const {
    platformInfo: { isOnlyMercado },
    付款订单量tips,
    订单损耗量tips,
    订单损耗率tips,
  } = useDashboardPlatformTips();

  useEffect(() => {
    if (!value || !chartDomRef.current || !filterCtx.platform || filterCtx.platform === 'ALL') {
      return () => {};
    }
    const type = getType(filterCtx.platform, tab);

    /**
     * 领先按钮点击事件
     * @param evt
     */
    const clickAheadButton = ({ dataIndex, seriesIndex }: any) => {
      if (seriesIndex !== 1 && seriesIndex !== 2) {
        return;
      }
      if (isNil(type)) {
        return;
      }
      const stages = keys(value?.[type]) as StatsName[];
      const performanceName = statsNameToPerformanceName(
        stages[dataIndex],
        !!isCod,
        filterCtx?.platform,
      );
      setTrendValue({ type: 'rank', visible: true, params: { performanceName } });
    };
    if (!chartRef.current) {
      chartRef.current = initEcharts(chartDomRef.current);
    }
    // 当数据没有匹配到后端返回内容时 默认返回shopee节点
    if (isNil(type)) {
      const defaultType =
        tab === 'COD订单损耗情况' ? 'shopeeCOD订单损耗情况' : 'shopee非COD订单损耗情况';
      const values = setEchartsDefaultValue(value)[defaultType];
      const { 揽收: _, ...others } = values;
      const options = convertOptions(
        isAeProduct ? others : values,
        t,
        rankEnableFlagr?.allowed,
        isWrap,
      );
      chartRef.current.setOption(options);
    }
    if (type) {
      const values = value[type];
      const { 揽收: _, ...others } = values || {};
      const options = convertOptions(
        isAeProduct
          ? others
          : isOnlyMercado
          ? pick(
              values,
              filterCtx.dateRange[1].isSameOrAfter(mercadoNewDataDate, 'days')
                ? ['付款', '打包', '卖家点击发货', '揽收', '买家收货', '订单结束']
                : ['付款', '卖家点击发货', '订单结束'],
            )
          : values,
        t,
        rankEnableFlagr?.allowed,
        isWrap,
      );

      chartRef.current.setOption(options);
    }
    chartRef.current.on('click', clickAheadButton);
    return () => {
      chartRef.current?.off('click', clickAheadButton);
    };
  }, [
    tab,
    value,
    t,
    filterCtx.platform,
    isCod,
    setTrendValue,
    rankEnableFlagr?.allowed,
    isWrap,
    isOnlyMercado,
    filterCtx.dateRange,
  ]);
  const { loss, ahead, rate, total } = useMemo(() => {
    const type = getType(filterCtx.platform, tab);
    if (!type || !value) {
      return {
        loss: 0,
        ahead: -1,
        rate: percentDisplay(0),
        total: 0,
      };
    }

    const orderComplete = value[type]?.订单结束;
    const total = orderComplete?.订单总量;
    const ahead = orderComplete?.领先比率 || 0;
    const loss = lossStats?.[type].totalLossCount;
    const rate = lossStats?.[type]?.totalLossRate;

    return {
      loss: numDisplay(loss),
      ahead,
      rate: Number.isNaN(rate) ? percentDisplay(0) : percentDisplay(rate),
      total: numDisplay(total),
    };
  }, [filterCtx.platform, lossStats, tab, value]);

  return (
    <Spin spinning={loading}>
      <Container>
        {!isOnlyMercado && (
          <Tabs
            onChange={(key) => {
              setTab(key as any);
            }}
            activeKey={tab}
            animated={false}
            tabBarExtraContent={
              <TabTips className="max-w-xl">
                <InfoCircleFilled />
                {t(
                  'ViewDashboard.订单损耗：订单创建后，有过取消订单、退货退款或者仅退款的申请会被计作损耗',
                )}
              </TabTips>
            }
          >
            <Tabs.TabPane tab={t('ViewDashboard.非COD订单损耗')} key="非COD订单损耗情况" />
            <Tabs.TabPane tab={t('ViewDashboard.COD订单损耗')} key="COD订单损耗情况" />
          </Tabs>
        )}
        <ContentContainer>
          <TitleContainer>
            <TitleLayout
              performanceName={isCod ? 'COD_OVERALL_LOSS_RATE' : 'NOT_COD_OVERALL_LOSS_RATE'}
            >
              <div className="sub-title">
                <span className="before" />
                {isOnlyMercado ? t('订单损耗过程分析') : t(`ViewDashboard.${tab}`)}
              </div>
            </TitleLayout>
          </TitleContainer>
          <Relative>
            <div className="left">
              <div className="item">
                <div className="title">
                  {isOnlyMercado ? t('ViewDashboard.付款订单量') : t('ViewDashboard.订单总量')}
                  <Tooltip title={付款订单量tips || t('ViewDashboard.订单创建总数量')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
                <div className="val">{total}</div>
              </div>
              <div className="item">
                <div className="title">
                  {t('ViewDashboard.订单损耗量')}
                  <Tooltip
                    title={
                      订单损耗量tips ||
                      t('ViewDashboard.自订单创建后，取消订单或退货退款订单总数量')
                    }
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>

                <div className="val">{loss}</div>
              </div>
              <div className="item wastage-cod-item">
                <div className="title">
                  {t('ViewDashboard.订单损耗率')}
                  <Tooltip
                    title={
                      订单损耗率tips ||
                      `${t('ViewDashboard.订单损耗量')}/${t('ViewDashboard.下单总量')}`
                    }
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
                <div className="val">
                  {rate}
                  <AheadButton
                    rate={ahead}
                    onClick={() => {
                      setTrendValue({
                        type: 'rank',
                        params: {
                          performanceName: isCod
                            ? 'COD_OVERALL_LOSS_RATE'
                            : 'NOT_COD_OVERALL_LOSS_RATE',
                        },
                        visible: true,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
            <Divider
              className={css`
                margin: 16px 0;
              `}
            />
            <div className="el" ref={chartDomRef} />
          </Relative>
        </ContentContainer>
      </Container>
    </Spin>
  );
};

export default Wastage;
